import { AddCommentOutlined, ArticleOutlined, PsychologyAltOutlined } from '@mui/icons-material';
import * as React from "react";
import { Popover } from "radix-ui";
import { useTextSelection } from "@/hooks/ui/useTextSelection";
import { CONTRACT_SECTIONS } from "./Variables/variables.config";
import { SectionCard } from "./Editor/SectionCard";

interface ContractEditViewProps {
  variables: Record<string, string>;
  onVariablesChange: (variables: Record<string, string>) => void;
}

export const ContractEditView: React.FC<ContractEditViewProps> = ({ 
  variables, 
  onVariablesChange 
}) => {
  const composerRef = React.useRef<HTMLDivElement | null>(null);
  const { text, rect } = useTextSelection(composerRef as React.RefObject<HTMLElement>);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    setOpen(Boolean(text && rect));
  }, [text, rect]);

  function handleSelectOption(key: string, value: string) {
    const newVariables = { ...variables, [key]: value };
    onVariablesChange(newVariables);
  }

  return (
    <div className="p-4 md:py-10 md:pl-16 w-full min-h-screen">
      <div className="px-8 py-4 border border-white/10 rounded-lg">
        {/* composer heading */}
        <div className="flex items-center gap-2">
          <ArticleOutlined className="size-6 text-neutral-400" />
          <h2 className="text-2xl font-bold my-4">Contract Composer</h2>
        </div>

        {/* composer container */}
        <div ref={composerRef} className=''>
          <div>
            {/* Introduction Section */}
            <div className="p-6 rounded-2xl bg-[rgba(255,255,255,0.025)] my-4 space-y-4">
              <div>
                <h2 className="text-xl text-white mb-2">Position and Duties</h2>
                <p className="text-neutral-400">
                  Employee agrees to serve as Contractor during the fixed-term and to perform duties as assigned by the Company. Employee will work remote on a full-time basis and devote their best efforts to the performance of their duties.
                </p>
              </div>

              {CONTRACT_SECTIONS.map(section => (
                <SectionCard
                  key={section.id}
                  section={section}
                  variables={variables}
                  onVariableChange={handleSelectOption}
                />
              ))}
            </div>

            {/* Additional Section */}
            <div className="p-6 rounded-2xl bg-[rgba(255,255,255,0.025)] my-4 space-y-4">
              <p className="text-neutral-400">
                Employee agrees to serve as Contractor during the fixed-term and to perform duties as assigned by the Company. Employee will work remote on a full-time basis and devote their best efforts to the performance of their duties.
              </p>
            </div>

            {/* More Sections */}
            <div className="space-y-4">
              {CONTRACT_SECTIONS.map(section => (
                <SectionCard
                  key={`repeat-${section.id}`}
                  section={section}
                  variables={variables}
                  onVariableChange={handleSelectOption}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* selection popover */}
      {rect && (
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Anchor asChild>
            <div
              style={{
                position: 'fixed',
                top: rect.top,
                left: rect.left + rect.width / 2,
                width: 0,
                height: 0,
              }}
            />
          </Popover.Anchor>
          <Popover.Content
            side="top"
            align="center"
            sideOffset={10}
            className="z-[60] rounded-xl ring-1 ring-white/30 bg-neutral-900/90 p-1 text-sm text-white shadow-md backdrop-blur-sm"
          >
            <div className="flex items-center gap-1">
              <button
                aria-label="Explain"
                className="flex items-center gap-2 rounded-md hover:bg-white/10 px-2 py-1 hover:bg-neutral-700 focus:outline-none"
              >
                <PsychologyAltOutlined width={12} height={12} className="w-3 h-3" />
                Explain
              </button>
              <button
                aria-label="Comment"
                className="flex items-center gap-2 rounded-md hover:bg-white/10 px-2 py-1 hover:bg-neutral-700 focus:outline-none"
              >
                <AddCommentOutlined width={12} height={12} className="w-3 h-3" />
                Comment
              </button>
            </div>
            <Popover.Arrow className="fill-neutral-800 drop-shadow-[0_1px_0_var(--border)]" />
          </Popover.Content>
        </Popover.Root>
      )}
    </div>
  );
};
